// GameSettingsPanel.tsx
import React from "react";
import { Piece } from "../../types/Board.ts";
import { PIECE_DEFINITIONS } from "../../data/pieceDefinitions.ts";

interface GameSettingsPanelProps {
  // Promotion Options props
  availablePromotionPieces: string[];
  boardRows: number;
  pieces: Map<string, Piece>;

  // Move Rules props
  fiftyMoveLimit: number;
  seventyFiveMoveLimit: number;
  halfmoveClock: number;
  setFiftyMoveLimit: (limit: number) => void;
  setSeventyFiveMoveLimit: (limit: number) => void;

  // Capture rules
  diagonalCaptureOnly: boolean;
  setDiagonalCaptureOnly: (value: boolean) => void;

  // Show opponent moves
  showOpponentMoves: boolean;
  setShowOpponentMoves: (value: boolean) => void;

  // Clock props
  clockEnabled: boolean;
  clockDuration: number;
  whiteTimeLeft: number;
  blackTimeLeft: number;
  clockRunning: boolean;
  clockStarted: boolean;
  currentTurn: "white" | "black";
  toggleClock: (enabled: boolean) => void;
  handleClockDurationChange: (minutes: number) => void;
  startClock: () => void;
  resetClock: () => void;
  gameMode?: "local" | "vsAI" | "online";
}

export const GameSettingsPanel: React.FC<GameSettingsPanelProps> = ({
  availablePromotionPieces,
  boardRows,
  pieces,
  fiftyMoveLimit,
  seventyFiveMoveLimit,
  halfmoveClock,
  setFiftyMoveLimit,
  setSeventyFiveMoveLimit,
  diagonalCaptureOnly,
  setDiagonalCaptureOnly,
  showOpponentMoves,
  setShowOpponentMoves,
  clockEnabled,
  clockDuration,
  whiteTimeLeft,
  blackTimeLeft,
  clockRunning,
  clockStarted,
  currentTurn,
  toggleClock,
  handleClockDurationChange,
  startClock,
  resetClock,
  gameMode = "local",
}) => {
  const [showPromotionDetails, setShowPromotionDetails] = React.useState<boolean>(false);
  const [showMoveRulesSettings, setShowMoveRulesSettings] = React.useState<boolean>(false);
  const [showSpecialFeatures, setShowSpecialFeatures] = React.useState<boolean>(false);

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs
      .toString().padStart(2, "0")}`;
  };

  const handleFiftyMoveLimitChange = (newLimit: number) => {
    setFiftyMoveLimit(newLimit);
    const minimum75MoveLimit = Math.max(75, newLimit + 25);
    if (seventyFiveMoveLimit < minimum75MoveLimit) {
      setSeventyFiveMoveLimit(minimum75MoveLimit);
    }
  };

  return (
    <div
      style={{
        backgroundColor: "#f5f5f5",
        borderRadius: "8px",
        border: "2px solid #333",
        padding: "10px",
      }}
    >
      <h3 style={{ margin: "0 0 12px 0", color: "#333", fontSize: "14px" }}>
        ⚙️ Game Details:
      </h3>

      {/* Promotion Options Section */}
      <div style={{ marginBottom: "10px" }}>
        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: "4px",
            cursor: "pointer",
            marginBottom: "6px",
          }}
        >
          <input
            type="checkbox"
            checked={showPromotionDetails}
            onChange={(e) => setShowPromotionDetails(e.target.checked)}
            style={{ width: "16px", height: "16px", cursor: "pointer" }}
          />
          <span style={{ fontSize: "11px", fontWeight: "bold" }}>
            Show Promotion Details
          </span>
        </label>

        {showPromotionDetails && (
          <div
            style={{
              marginTop: "6px",
              padding: "7px",
              backgroundColor: "#fff",
              borderRadius: "4px",
              border: "1px solid #ddd",
            }}
          >
            <p style={{ fontSize: "13px", color: "#666", margin: "0 0 7px" }}>
              Available pieces for promotion:
            </p>
            <div
              style={{
                display: "flex",
                flexWrap: "nowrap",
                gap: "4px",
                justifyContent: "space-between",
              }}
            >
              {availablePromotionPieces.map((pieceType) => {
                const pieceDefinition = PIECE_DEFINITIONS[pieceType];
                if (!pieceDefinition) return null;

                let actualValue = 0;

                try {
                  const abbreviation = pieceDefinition.abbreviation.toUpperCase();
                  const boardSizeStr = `${boardRows}×${boardRows}`;

                  const pieceValuesModule = require("../../data/pieceValues.ts");
                  const pieceValues = pieceValuesModule.PIECE_VALUES?.[abbreviation];

                  if (pieceValues) {
                    const valueEntry = pieceValues.find(
                      (v: any) => v.boardSize === boardSizeStr
                    );
                    if (valueEntry) {
                      actualValue = valueEntry.value;
                    }
                  }
                } catch (error) {
                  console.warn(`Could not fetch value for ${pieceType}:`, error);
                }

                if (actualValue === 0) {
                  for (const [_, piece] of pieces) {
                    if (
                      piece.type &&
                      piece.type.toLowerCase() === pieceType.toLowerCase() &&
                      piece.value !== undefined
                    ) {
                      actualValue = piece.value;
                      break;
                    }
                  }
                }

                if (actualValue === 0 && pieceDefinition.value) {
                  actualValue = pieceDefinition.value;
                }

                return (
                  <div
                    key={pieceType}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      padding: "4px 2px",
                      backgroundColor: "#f5f5f5",
                      borderRadius: "4px",
                      fontSize: "8px",
                      fontWeight: "bold",
                      color: "#333",
                      border: "2px solid #333",
                      flex: "1 1 0",
                      minWidth: "0",
                      gap: "2px",
                    }}
                  >
                    <img
                      src={`/pieces/${pieceDefinition.abbreviation.toLowerCase()}.png`}
                      alt={pieceType}
                      style={{
                        width: "41px",
                        height: "41px",
                        filter: "brightness(0) saturate(100%)",
                      }}
                    />
                    <div
                      style={{
                        fontSize: "10px",
                        fontWeight: "bold",
                        textAlign: "center",
                        lineHeight: "1.1",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        width: "100%",
                        padding: "0 2px",
                      }}
                    >
                      {pieceType.charAt(0).toUpperCase() + pieceType.slice(1)}
                    </div>
                    <div
                      style={{
                        fontSize: "10px",
                        color: "#fff",
                        backgroundColor: "#333",
                        padding: "1px 3px",
                        borderRadius: "3px",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {actualValue.toFixed(2)}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Divider */}
      <div
        style={{
          height: "1px",
          backgroundColor: "#ddd",
          margin: "10px 0",
        }}
      />

      {/* Move Rules Section */}
      <div style={{ marginBottom: "10px" }}>
        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: "4px",
            cursor: "pointer",
            marginBottom: "6px",
          }}
        >
          <input
            type="checkbox"
            checked={showMoveRulesSettings}
            onChange={(e) => setShowMoveRulesSettings(e.target.checked)}
            style={{ width: "16px", height: "16px", cursor: "pointer" }}
          />
          <span style={{ fontSize: "11px", fontWeight: "bold" }}>
            Show 50/75 Move Rules Settings
          </span>
        </label>

        {showMoveRulesSettings && (
          <div
            style={{
              marginTop: "6px",
              padding: "7px",
              backgroundColor: "#fff",
              borderRadius: "4px",
              border: "1px solid #ddd",
            }}
          >
            <div style={{ marginBottom: "10px" }}>
              <label
                style={{
                  display: "block",
                  fontSize: "10px",
                  fontWeight: "bold",
                  color: "#333",
                  marginBottom: "4px",
                }}
              >
                50-Move Rule Limit:
              </label>
              <input
                type="number"
                min="1"
                max="200"
                value={fiftyMoveLimit}
                onChange={(e) =>
                  handleFiftyMoveLimitChange(parseInt(e.target.value) || 50)
                }
                style={{
                  width: "100%",
                  padding: "6px",
                  borderRadius: "4px",
                  border: "1px solid #ddd",
                  fontSize: "11px",
                }}
              />
              <p
                style={{
                  fontSize: "11px",
                  color: "#666",
                  margin: "4px 0 0",
                }}
              >
                Player can claim draw after {fiftyMoveLimit} moves without
                capture / pawn move
              </p>
            </div>

            <div style={{ marginBottom: "10px" }}>
              <label
                style={{
                  display: "block",
                  fontSize: "10px",
                  fontWeight: "bold",
                  color: "#333",
                  marginBottom: "4px",
                }}
              >
                75-Move Rule Limit:
              </label>
              <input
                type="number"
                min={Math.max(75, fiftyMoveLimit + 25)}
                max="300"
                value={seventyFiveMoveLimit}
                onChange={(e) =>
                  setSeventyFiveMoveLimit(parseInt(e.target.value) || 75)
                }
                style={{
                  width: "100%",
                  padding: "6px",
                  borderRadius: "4px",
                  border: "1px solid #ddd",
                  fontSize: "11px",
                }}
              />
              <p
                style={{
                  fontSize: "11px",
                  color: "#666",
                  margin: "4px 0 0",
                }}
              >
                Automatic draw after {seventyFiveMoveLimit} moves without
                capture / pawn move
              </p>
            </div>

            <div
              style={{
                marginTop: "7px",
                padding: "7px",
                backgroundColor: "#fff3cd",
                borderRadius: "4px",
                border: "1px solid #ffc107",
              }}
            >
              <p
                style={{
                  fontSize: "11px",
                  color: "#856404",
                  margin: 0,
                  fontWeight: "bold",
                }}
              >
                Current Halfmove Clock: {halfmoveClock}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Divider */}
      <div
        style={{
          height: "1px",
          backgroundColor: "#ddd",
          margin: "10px 0",
        }}
      />

      {/* Special Features Section */}
      <div style={{ marginBottom: "10px" }}>
        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: "4px",
            cursor: "pointer",
            marginBottom: "6px",
          }}
        >
          <input
            type="checkbox"
            checked={showSpecialFeatures}
            onChange={(e) => setShowSpecialFeatures(e.target.checked)}
            style={{ width: "16px", height: "16px", cursor: "pointer" }}
          />
          <span style={{ fontSize: "11px", fontWeight: "bold" }}>
            Special Features
          </span>
        </label>

        {showSpecialFeatures && (
          <div
            style={{
              marginTop: "6px",
              padding: "7px",
              backgroundColor: "#fff",
              borderRadius: "4px",
              border: "1px solid #ddd",
            }}
          >
            <div style={{ marginBottom: "8px" }}>
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  cursor: "not-allowed",
                  opacity: 0.7,
                }}
              >
                <input
                  type="checkbox"
                  checked={boardRows === 8}
                  disabled={true}
                  style={{ 
                    width: "14px", 
                    height: "14px", 
                    cursor: "not-allowed",
                  }}
                />
                <span style={{ fontSize: "11px", color: "#333" }}>
                  En Passant
                </span>
              </label>
              <p
                style={{
                  fontSize: "11px",
                  color: "#666",
                  margin: "2px 0 0 20px",
                  fontStyle: "italic",
                }}
              >
                (Only available for 8×8 games)
              </p>
            </div>

            <div style={{ marginBottom: "8px" }}>
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  cursor: "not-allowed",
                  opacity: 0.7,
                }}
              >
                <input
                  type="checkbox"
                  checked={boardRows === 8 || boardRows === 9}
                  disabled={true}
                  style={{ 
                    width: "14px", 
                    height: "14px", 
                    cursor: "not-allowed",
                  }}
                />
                <span style={{ fontSize: "11px", color: "#333" }}>
                  Castling
                </span>
              </label>
              <p
                style={{
                  fontSize: "11px",
                  color: "#666",
                  margin: "2px 0 0 20px",
                  fontStyle: "italic",
                }}
              >
                (Only available for 8×8 and 9×9 games)
              </p>
            </div>

            <div>
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  cursor: "not-allowed",
                  opacity: 0.7,
                }}
              >
                <input
                  type="checkbox"
                  checked={boardRows === 9 || boardRows === 11}
                  disabled={true}
                  style={{
                    width: "14px",
                    height: "14px",
                    cursor: "not-allowed",
                  }}
                />
                <span style={{ fontSize: "11px", color: "#333" }}>
                  Slide Bishop, Preist & Nun
                </span>
              </label>
              <p
                style={{
                  fontSize: "11px",
                  color: "#666",
                  margin: "2px 0 0 20px",
                  fontStyle: "italic",
                }}
              >
                (Only available for 9×9 and 11×11 games )
              </p>
            </div>

            {/* Divider within Special Features */}
            <div
              style={{
                height: "1px",
                backgroundColor: "#ddd",
                margin: "8px 0",
              }}
            />

            <div style={{ marginBottom: "8px" }}>
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  cursor: "pointer",
                }}
              >
                <input
                  type="checkbox"
                  checked={diagonalCaptureOnly}
                  onChange={(e) => setDiagonalCaptureOnly(e.target.checked)}
                  style={{
                    width: "14px",
                    height: "14px",
                    cursor: "pointer",
                  }}
                />
                <span style={{ fontSize: "11px", color: "#333" }}>
                  Knave/Squire: Diagonal Capture Only
                </span>
              </label>
              <p
                style={{
                  fontSize: "11px",
                  color: "#666",
                  margin: "2px 0 0 20px",
                  fontStyle: "italic",
                }}
              >
                {diagonalCaptureOnly
                  ? "(Knave/Squire can only capture diagonally)"
                  : "(Knave/Squire can capture forward and diagonally)"}
              </p>
            </div>

            <div>
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  cursor: "pointer",
                }}
              >
                <input
                  type="checkbox"
                  checked={showOpponentMoves}
                  onChange={(e) => setShowOpponentMoves(e.target.checked)}
                  style={{
                    width: "14px",
                    height: "14px",
                    cursor: "pointer",
                  }}
                />
                <span style={{ fontSize: "11px", color: "#333" }}>
                  Show Opponent Piece Moves
                </span>
              </label>
              <p
                style={{
                  fontSize: "11px",
                  color: "#666",
                  margin: "2px 0 0 20px",
                  fontStyle: "italic",
                }}
              >
                {showOpponentMoves
                  ? "(Click opponent pieces to see their possible moves)"
                  : "(Opponent piece moves are hidden)"}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Divider */}
      <div
        style={{
          height: "1px",
          backgroundColor: "#ddd",
          margin: "10px 0",
        }}
      />

      {/* Game Clock Section */}
      <div>
        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: "4px",
            cursor: gameMode === "vsAI" ? "not-allowed" : "pointer",
            marginBottom: "7px",
            opacity: gameMode === "vsAI" ? 0.6 : 1,
          }}
          title={gameMode === "vsAI" ? "Game clock is not available when playing vs AI" : ""}
        >
          <input
            type="checkbox"
            checked={clockEnabled && gameMode !== "vsAI"}
            onChange={(e) => toggleClock(e.target.checked)}
            disabled={gameMode === "vsAI"}
            style={{
              width: "16px",
              height: "16px",
              cursor: gameMode === "vsAI" ? "not-allowed" : "pointer"
            }}
          />
          <span style={{ fontSize: "11px", fontWeight: "bold" }}>
            Enable Game Clock
          </span>
        </label>

        {gameMode === "vsAI" && (
          <div
            style={{
              padding: "6px 8px",
              backgroundColor: "#fff3cd",
              borderRadius: "4px",
              border: "1px solid #ffc107",
              marginBottom: "7px",
            }}
          >
            <p
              style={{
                fontSize: "10px",
                color: "#856404",
                margin: 0,
                fontStyle: "italic",
              }}
            >
              ⚠️ Game clock is not available when playing vs Computer (AI)
            </p>
          </div>
        )}

        {clockEnabled && gameMode !== "vsAI" && (
          <>
            <div style={{ marginBottom: "7px" }}>
              <label
                style={{
                  display: "block",
                  fontSize: "10px",
                  fontWeight: "bold",
                  color: "#333",
                  marginBottom: "4px",
                }}
              >
                Time Control (minutes):
              </label>
              <input
                type="number"
                min="1"
                max="180"
                value={clockDuration}
                onChange={(e) =>
                  handleClockDurationChange(parseInt(e.target.value) || 30)
                }
                disabled={clockStarted}
                style={{
                  width: "100%",
                  padding: "6px",
                  borderRadius: "4px",
                  border: "1px solid #ddd",
                  fontSize: "11px",
                  cursor: clockStarted ? "not-allowed" : "text",
                  backgroundColor: clockStarted ? "#f0f0f0" : "white",
                }}
              />
            </div>

            <div
              style={{
                display: "flex",
                gap: "7px",
                marginBottom: "7px",
              }}
            >
              <div style={{ flex: 1 }}>
                <div
                  style={{
                    fontSize: "10px",
                    fontWeight: "bold",
                    color: "#666",
                    marginBottom: "4px",
                    textAlign: "center",
                  }}
                >
                  ⚪ White
                </div>
                <div
                  style={{
                    padding: "8px",
                    backgroundColor:
                      currentTurn === "white" && clockRunning
                        ? "#4CAF50"
                        : "#e0e0e0",
                    color:
                      currentTurn === "white" && clockRunning
                        ? "white"
                        : "#333",
                    borderRadius: "4px",
                    fontSize: "14px",
                    fontWeight: "bold",
                    textAlign: "center",
                    fontFamily: "monospace",
                    border: "2px solid",
                    borderColor:
                      currentTurn === "white" && clockRunning
                        ? "#45a049"
                        : "#bdbdbd",
                  }}
                >
                  {formatTime(whiteTimeLeft)}
                </div>
              </div>

              <div style={{ flex: 1 }}>
                <div
                  style={{
                    fontSize: "10px",
                    fontWeight: "bold",
                    color: "#666",
                    marginBottom: "4px",
                    textAlign: "center",
                  }}
                >
                  ⚫ Black
                </div>
                <div
                  style={{
                    padding: "8px",
                    backgroundColor:
                      currentTurn === "black" && clockRunning
                        ? "#4CAF50"
                        : "#e0e0e0",
                    color:
                      currentTurn === "black" && clockRunning
                        ? "white"
                        : "#333",
                    borderRadius: "4px",
                    fontSize: "14px",
                    fontWeight: "bold",
                    textAlign: "center",
                    fontFamily: "monospace",
                    border: "2px solid",
                    borderColor:
                      currentTurn === "black" && clockRunning
                        ? "#45a049"
                        : "#bdbdbd",
                  }}
                >
                  {formatTime(blackTimeLeft)}
                </div>
              </div>
            </div>

            <div style={{ display: "flex", gap: "7px" }}>
              {!clockStarted ? (
                <button
                  onClick={startClock}
                  style={{
                    flex: 1,
                    padding: "7px",
                    backgroundColor: "#4CAF50",
                    color: "white",
                    border: "none",
                    borderRadius: "4px",
                    cursor: "pointer",
                    fontSize: "11px",
                    fontWeight: "bold",
                  }}
                >
                  Start Clock
                </button>
              ) : (
                <button
                  onClick={() => {
                    /* Clock runs automatically */
                  }}
                  disabled
                  style={{
                    flex: 1,
                    padding: "7px",
                    backgroundColor: "#4CAF50",
                    color: "white",
                    border: "none",
                    borderRadius: "4px",
                    cursor: "not-allowed",
                    fontSize: "11px",
                    fontWeight: "bold",
                    opacity: 0.7,
                  }}
                >
                  Clock Running
                </button>
              )}

              <button
                onClick={resetClock}
                style={{
                  flex: 1,
                  padding: "7px",
                  backgroundColor: "#ff9800",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontSize: "11px",
                  fontWeight: "bold",
                }}
              >
                Reset
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
